import { useCallback, useState } from "react";
import {
  getRepositoryPipelines,
  runPipeline,
  stopPipelineRun,
  getPipelineSteps as apiGetPipelineSteps,
  triggerPipelineStep as apiTriggerPipelineStep,
} from "../api";
import { useToast } from "./use-toast";

export const usePipelines = () => {
  const [pipelines, setPipelines] = useState({ definitions: [] });
  const [selectedPipelineConfig, setSelectedPipelineConfig] = useState(null);
  const [isTriggeringPipeline, setIsTriggeringPipeline] = useState(false);
  const { toast } = useToast();

  const fetchPipelines = useCallback(async (workspace, repoSlug) => {
    try {
      const pipelineData = await getRepositoryPipelines(workspace, repoSlug);
      setPipelines(pipelineData || { definitions: [] });
      return pipelineData;
    } catch (err) {
      console.error("Error fetching pipelines:", err);
      toast({
        title: "Error",
        description: "Failed to load pipelines",
        variant: "destructive",
      });
      return { definitions: [] };
    }
  }, [toast]);

  const triggerPipeline = useCallback(async (workspace, repoSlug, branch, pattern) => {
    try {
      setIsTriggeringPipeline(true);

      // Create pipeline config for the specific pattern
      const pipelineConfig = {
        selector: {
          type: "branches",
          pattern: pattern || "deploy to *",
        },
      };

      // Run the pipeline
      await runPipeline(workspace, repoSlug, branch, pipelineConfig);

      toast({
        title: "Success",
        description: "Pipeline triggered successfully",
      });

      return true;
    } catch (err) {
      toast({
        title: "Error",
        description:
          err.response?.data?.error?.message || "Failed to trigger pipeline",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsTriggeringPipeline(false);
    }
  }, [toast]);

  const stopPipeline = useCallback(async (workspace, repoSlug, pipelineUuid) => {
    try {
      await stopPipelineRun(workspace, repoSlug, pipelineUuid);
      toast({
        title: "Success",
        description: "Pipeline stopped successfully",
      });
      return true;
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to stop pipeline",
        variant: "destructive",
      });
      return false;
    }
  }, [toast]);

  // Get pipeline steps
  const getPipelineSteps = useCallback(async (workspace, repoSlug, pipelineUuid) => {
    try {
      const steps = await apiGetPipelineSteps(
        workspace,
        repoSlug,
        pipelineUuid
      );
      return steps;
    } catch (err) {
      console.error("Error fetching pipeline steps:", err);
      throw err;
    }
  }, []);

  // Trigger a pipeline step
  const triggerPipelineStep = useCallback(async (
    workspace,
    repoSlug,
    pipelineUuid,
    stepUuid
  ) => {
    try {
      const result = await apiTriggerPipelineStep(
        workspace,
        repoSlug,
        pipelineUuid,
        stepUuid
      );
      return result;
    } catch (err) {
      console.error("Error triggering pipeline step:", err);
      throw err;
    }
  }, []);

  return {
    pipelines,
    selectedPipelineConfig,
    setSelectedPipelineConfig,
    isTriggeringPipeline,
    fetchPipelines,
    triggerPipeline,
    stopPipeline,
    getPipelineSteps,
    triggerPipelineStep,
  };
};
